import React, { useMemo, useState } from 'react';
import { MultiSelect } from '@mantine/core';
import { FieldProps, buildSelectOptionItems, stripSelectOptionValueSuffix, SelectOptionItem } from '../form-helpers';
import { SelectCaret } from './SelectCaret';

export type FormFieldMultiSelectProps = Omit<FieldProps, 'value' | 'onChange'> & {
    value: string[];
    onChange: (value: string[]) => void;
    availableOptions: string[];
};

function FormFieldMultiSelect({ label, required, value, onChange, availableOptions, error, disabled, inTable }: FormFieldMultiSelectProps) {
    const [dropdownOpened, setDropdownOpened] = useState(false);

    // `availableOptions` may contain duplicate labels (real form definitions do) — give each
    // occurrence its own unique `value` so Mantine can render them as separate options instead of
    // colliding on a duplicate key. See `buildSelectOptionItems` for why.
    const data = useMemo(() => buildSelectOptionItems(availableOptions), [availableOptions]);

    // Our own `value`/`onChange` contract is a plain label array, possibly with duplicates (e.g.
    // both "test" entries picked) — unaffected by the above, since decode/encode, validation and
    // autosave never see the suffixed keys. Each selected label claims the first not-yet-claimed
    // data entry with a matching label, so two picked duplicates map to two distinct data keys
    // (letting Mantine show both as selected) rather than colliding on the same one.
    const selectedValues = useMemo(() => {
        const remaining = [...data];
        return (value ?? []).map((label) => {
            const index = remaining.findIndex((item) => item.label === label);
            if (index === -1) return label;
            const [claimed] = remaining.splice(index, 1) as [SelectOptionItem];
            return claimed.value;
        });
    }, [data, value]);

    return (
        <MultiSelect
            label={label}
            required={required}
            data={data}
            value={selectedValues}
            onChange={(vals) => onChange(vals.map(stripSelectOptionValueSuffix))}
            error={error}
            disabled={disabled}
            mt="sm"
            maw={inTable ? '100%' : undefined}
            onDropdownOpen={() => setDropdownOpened(true)}
            onDropdownClose={() => setDropdownOpened(false)}
            rightSection={<SelectCaret dropdownOpened={dropdownOpened} showClear={false} onClear={() => {}} />}
        />
    );
}

export default React.memo(FormFieldMultiSelect);
